import { PROTOCOLS } from '../../site-gateway/models/gateway';
import { Device, DeviceProperty, EditableSettingsProperty, ResolvedSection } from '../models/DeviceDetail';
import { DeviceDetailOverviewVm } from '../models/DeviceDetailOverviewVm';
import { SECTION_TYPES } from '../models/SectionType';
import { UiProfile, UiProfileProperty } from '../models/UiProfile';

import { SECTIONS_EXCLUDED_FROM_DIAGNOSTIC } from './build-device-sections';
import { buildOverviewProperties } from './build-overview';
import buildEditor, { DeviceEditablePropertyValue, SectionItemEditor } from './editor-builder/buildEditor';
import { computeZigbeeSectionProperties, enrichUiProperty } from './enrich-ui-property';

/** The sections of the UI profile that show at least one property of the device. */
export function resolveSections(device: Device, uiProfile: UiProfile): ResolvedSection[] {
    const isZigbee = device.deviceUrl?.startsWith('zigbee');
    const propertyKeys = isZigbee
        ? Object.keys(device.properties).map((propertyId) => `${device.id}.${propertyId}`)
        : [];

    return (uiProfile.sections ?? [])
        .map((section) => ({
            title: section.title,
            propertyIds: isZigbee
                ? computeZigbeeSectionProperties(device.id, propertyKeys, section)
                : section.properties,
        }))
        .filter((section) => section.propertyIds.length > 0);
}

export function diagnosticSections(sections: ResolvedSection[]): ResolvedSection[] {
    return sections.filter((section) => !SECTIONS_EXCLUDED_FROM_DIAGNOSTIC.has(section.title));
}

export function settingsSections(sections: ResolvedSection[]): ResolvedSection[] {
    return sections.filter((section) => section.title === SECTION_TYPES.SETTINGS);
}

export function buildDeviceOverviewVm(
    device: Device,
    uiProfile: UiProfile,
    sections: ResolvedSection[],
): DeviceDetailOverviewVm {
    const presentationSections = sections.filter((section) => section.title === SECTION_TYPES.PRESENTATION);
    const { radioRangeValue, properties } = buildOverviewProperties(
        presentationSections,
        uiProfile,
        device.properties,
        device.lastUpdateTime,
    );

    return {
        id: device.id,
        name: device.name,
        icon: device.icon,
        available: device.available,
        protocol: PROTOCOLS.find((protocol) => device.deviceUrl?.includes(protocol)) ?? null,
        radioRangeValue,
        properties,
    };
}

export function buildEditableSettings(
    device: Device,
    uiProfile: UiProfile,
    sections: ResolvedSection[],
): EditableSettingsProperty[] {
    return settingsSections(sections).flatMap((section) =>
        section.propertyIds
            .map((propertyId) => {
                const rawUiProperty = uiProfile.properties[propertyId];
                if (!rawUiProperty?.editable) return null;
                const deviceProperty = device.properties[propertyId];
                return {
                    propertyId,
                    uiProperty: enrichUiProperty(rawUiProperty, deviceProperty?.metadata),
                    deviceProperty,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item != null),
    );
}

/** Undefined when the value is not one an editor can handle. */
export function buildPropertyEditor(
    uiProperty: UiProfileProperty | undefined,
    property: DeviceProperty,
): SectionItemEditor | undefined {
    if (!uiProperty) return undefined;
    if (property.value != null && !['string', 'number'].includes(typeof property.value)) {
        return undefined;
    }
    return buildEditor(uiProperty, property.value as DeviceEditablePropertyValue, property.metadata);
}
